import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useFlag } from 'flags';
import { Formik, Field, Form } from 'formik';

import { Card } from 'components/Card/Card';
import Button from 'components/Button/Button';
import Tooltip from 'components/Tooltip/Tooltip';

import Loading from 'components/Loading/Loading';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import colors from 'theme/Colors.module.scss';
import './UserDetail.scss';

import {
  Box,
  Text,
  Flex,
  Input,
  FormLabel,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';

import { userValidation } from 'modules/users/userValidations';

import {
  useGetUserByIdQuery,
  useUpdateUserByIdMutation,
} from 'modules/users/usersApi';

export default () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const isReadOnly = useFlag(['features', 'isReadOnly']);
  const isAdmin = useFlag(['features', 'isAdmin']);
  const disabled = isReadOnly || !isAdmin || true; // disabled for now

  const {
    data: userData,
    error,
    isLoading,
  } = useGetUserByIdQuery(id, {
    skip: !id, // Skip the query if companyId is null or undefined
  });

  const [updateUserById] = useUpdateUserByIdMutation();

  const handleSubmit = async (values, form) => {
    form.setSubmitting(true);

    try {
      await updateUserById({ id, data: values });
    } catch (err) {
      console.error(err);
    } finally {
      form.setSubmitting(false);
    }
  };

  if (error) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to update user data',
      })
    );
  }

  if (isLoading) {
    return <Loading type="fullpage" />;
  }

  return (
    <>
      <Box className="user-detail-header">
        <Box className="user-detail-header__left">
          <Button
            type="button"
            url="/users"
            icon="back"
            text="Back"
            textColor={colors.slate}
            btnStyle="transparent"
          />
        </Box>
        <Box className="user-detail-header__nav">
          <Text as="p" fontWeight="600">
            Manage User
          </Text>
        </Box>
        <Box className="user-detail-header__right"></Box>
      </Box>
      {userData ? (
        <Formik
          enableReinitialize
          validateOnMount
          initialValues={userData}
          validationSchema={userValidation}
          onSubmit={handleSubmit}
        >
          {form => {
            const { isSubmitting, dirty } = form;

            return (
              <Form className="user-detail-form__container">
                <Card className="user-detail-form__card">
                  <Text as="p" fontWeight="500">
                    User Information
                  </Text>
                  <Flex flexDirection="column" gap={8} mt={8}>
                    <Flex gap={8} flexWrap="wrap">
                      <Flex flexDirection="column" flex="1 1 300px" minW="0">
                        <Field name="firstname">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.firstname && form.touched.firstname
                              }
                            >
                              <FormLabel>First Name</FormLabel>
                              <Input
                                {...field}
                                placeholder="First Name*"
                                focusBorderColor={colors.primary600}
                                disabled={disabled || isSubmitting}
                              />
                              <FormErrorMessage>
                                {form.errors.firstname}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </Flex>
                      <Flex flexDirection="column" flex="1 1 300px" minW="0">
                        <Field name="lastname">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.lastname && form.touched.lastname
                              }
                            >
                              <FormLabel>Last Name</FormLabel>
                              <Input
                                {...field}
                                placeholder="Last Name*"
                                focusBorderColor={colors.primary600}
                                disabled={disabled || isSubmitting}
                              />
                              <FormErrorMessage>
                                {form.errors.lastname}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </Flex>
                    </Flex>

                    <Flex flexDirection="column">
                      <Field name="email">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={form.errors.email && form.touched.email}
                          >
                            <FormLabel>
                              Email&nbsp;
                              <Tooltip
                                tipText="Updating your email address affects the login credentials."
                                direction="right"
                              />
                            </FormLabel>
                            <Input
                              {...field}
                              type="email"
                              placeholder="Email*"
                              focusBorderColor={colors.primary600}
                              disabled={disabled || isSubmitting}
                            />
                            <FormErrorMessage>
                              {form.errors.email}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>
                  </Flex>
                </Card>

                {!disabled && (
                  <Flex flexDirection="column" align="center">
                    <Button
                      type="submit"
                      text="Update User"
                      textColor={colors.white}
                      btnStyle="solid"
                      hoverColor={colors.primary500}
                      textHoverColor={colors.white}
                      disabled={disabled || isSubmitting || !dirty}
                    />
                    <Text as="p" className="footnote">
                      An asterisk (*) marks a required field.
                    </Text>
                  </Flex>
                )}
              </Form>
            );
          }}
        </Formik>
      ) : (
        'Not found'
      )}
    </>
  );
};
